<template>
  <!-- Closed: non-interactive "Closed" halves -->
  <div v-if="closed" class="font-medium flex flex-1 gap-1 w-full px-4" aria-label="Closed for trading">
    <div class="flex-1 rounded-l-[4px] h-[48px] flex items-center p-2 bg-surface-low">
      <p class="flex-1 font-semibold text-base leading-5 text-center truncate text-on-surface-tertiary">
        {{ t('closed') }}
      </p>
    </div>
    <div class="flex-1 rounded-r-[4px] h-[48px] flex items-center p-2 bg-surface-low">
      <p class="flex-1 font-semibold text-base leading-5 text-center truncate text-on-surface-tertiary">
        {{ t('closed') }}
      </p>
    </div>
  </div>
  <div v-else class="font-medium flex flex-1 gap-1 tabular-nums w-full px-4 [@media(hover:none)]:pointer-events-none">
    <div
      @click.stop="handleBidClick(yesConid)"
      :class="[
        'flex-1 rounded-l-[4px] h-[48px] flex items-center p-2',
        yesBidValue ? 'bg-(--p-yes-subtle)' : 'bg-(--p-neutral10) dark:bg-(--p-neutral90) opacity-60',
      ]"
    >
      <p
        :class="[
          'flex-1 font-semibold text-base leading-5 text-center truncate',
          yesBidValue ? 'text-(--p-yes)' : 'text-(--p-text-muted-color) opacity-60',
        ]"
      >
        {{ t('yes') }} {{ yesBid }}
      </p>
    </div>
    <div
      @click.stop="handleBidClick(noConid)"
      :class="[
        'flex-1 rounded-r-[4px] h-[48px] flex items-center p-2',
        noBidValue ? 'bg-(--p-no-subtle)' : 'bg-(--p-neutral10) dark:bg-(--p-neutral90) opacity-60',
      ]"
    >
      <p
        :class="[
          'flex-1 font-semibold text-base leading-5 text-center truncate',
          noBidValue ? 'text-(--p-no)' : 'text-(--p-text-muted-color) opacity-60',
        ]"
      >
        {{ t('no') }} {{ noBid }}
      </p>
    </div>
  </div>

  <!-- Animated Bid/Ask Meter (kept as an empty spacer when closed to preserve card height) -->
  <div class="w-full h-5 flex-shrink-0 px-4 flex items-center">
    <div v-if="!closed" class="relative w-full h-3">
      <!-- Grey background -->
      <div class="absolute left-0 top-[3px] w-full h-[6px] bg-(--p-neutral10) dark:bg-(--p-neutral90) rounded-lg"></div>

      <!-- Green section (Yes) -->
      <div
        v-if="yesBidValue && yesBidValue > 0"
        class="absolute left-0 top-[3px] h-[6px] rounded-l-lg bg-(--p-yes)"
        :style="{ width: `${yesSectionWidth}%` }"
      ></div>

      <!-- Red section (No) -->
      <div
        v-if="noBidValue && noBidValue > 0"
        class="absolute right-0 top-[3px] rounded-r-lg h-[6px] bg-(--p-no)"
        :style="{ width: `${noSectionWidth}%` }"
      ></div>

      <!-- Circular handle -->
      <div
        :class="[
          'absolute top-0 w-3 h-3 rounded-full transform -translate-x-1/2 ease-in-out',
          hasValidData
            ? 'bg-white dark:bg-black border-(--p-neutral95) dark:border-(--p-neutral5) border-2'
            : 'bg-(--p-neutral70) dark:bg-(--p-neutral30) border-(--p-neutral60) dark:border-(--p-neutral40) border-2 opacity-60',
        ]"
        :style="{ left: `${meterRatio}%` }"
      ></div>

      <!-- Data unavailable indicator -->
      <div
        v-if="!hasValidData && !isDataLoading"
        class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xs text-(--p-text-muted-color) pointer-events-none"
      >
        No data
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDollarPrice } from '@/utils/formatPrice';
import { useFlexbestBidAskStream } from '@/composables/useFlexbestBidAskStream';
import { useOrderTicket } from '@/electionHub/composables/useOrderTicket';
import { useAppState } from '@/stores/useAppState';
import { usePriceSideSetting } from '@/composables/usePriceSideSetting';
import useContractActions from '@/composables/useContractActions';

export interface IllustratedBidAskProps {
  yesConid: number;
  noConid: number;
  yesConidExPairs?: string[];
  noConidExPairs?: string[];
  exchange: string;
  multiExchange?: boolean;
  priceSide?: 'bid' | 'ask';
  productConid?: number;
  /** Exchange paired with productConid for routing. Defaults to exchange. */
  routingExchange?: string;
  /** When false, bid-ask streaming is paused. Defaults to true. */
  isLive?: boolean;
  /** Seed ask price for YES side from product response (0-1) */
  seedAskYes?: number;
  /** Seed ask price for NO side from product response (0-1) */
  seedAskNo?: number;
  /** Seed bid price for YES side from product response (0-1) */
  seedBidYes?: number;
  /** Seed bid price for NO side from product response (0-1) */
  seedBidNo?: number;
  /** When true, render a non-interactive "Closed" state instead of Yes/No prices. */
  closed?: boolean;
}

const { t } = useI18n();
const props = withDefaults(defineProps<IllustratedBidAskProps>(), { isLive: true });

const appState = useAppState();

const yesConidExPairs = computed(() => props.yesConidExPairs ?? [`${props.yesConid}@${props.exchange}`]);
const noConidExPairs = computed(() => props.noConidExPairs ?? [`${props.noConid}@${props.exchange}`]);

const yesStream = useFlexbestBidAskStream(yesConidExPairs, {
  seedBid: () => props.seedBidYes,
  seedAsk: () => props.seedAskYes,
  isLive: () => props.isLive,
});
const noStream = useFlexbestBidAskStream(noConidExPairs, {
  seedBid: () => props.seedBidNo,
  seedAsk: () => props.seedAskNo,
  isLive: () => props.isLive,
});

const { priceSide: defaultPriceSide } = usePriceSideSetting();
const side = computed(() => props.priceSide ?? defaultPriceSide.value);
const yesBidValue = computed(() => (side.value === 'bid' ? yesStream.bid.value : yesStream.ask.value));
const noBidValue = computed(() => (side.value === 'bid' ? noStream.bid.value : noStream.ask.value));

const yesBid = computed(() => formatDollarPrice(yesBidValue.value, true));
const noBid = computed(() => formatDollarPrice(noBidValue.value, true));

const meterRatio = computed(() => {
  const yesValue = yesBidValue.value;
  const noValue = noBidValue.value;

  if (
    (yesValue === null || yesValue === undefined || yesValue <= 0) &&
    (noValue === null || noValue === undefined || noValue <= 0)
  ) {
    return 50;
  }

  if (yesValue === null || yesValue === undefined || yesValue <= 0) {
    return noValue ? (1 - noValue) * 100 : 0;
  }

  if (noValue === null || noValue === undefined || noValue <= 0) {
    return yesValue * 100;
  }

  const total = yesValue + noValue;
  return total > 0 ? (yesValue / total) * 100 : 50;
});

const yesSectionWidth = computed(() => {
  const yesValue = yesBidValue.value;
  const noValue = noBidValue.value;

  if (yesValue === null || yesValue === undefined || yesValue <= 0) return 0;

  if (noValue === null || noValue === undefined || noValue <= 0) {
    return yesValue * 100;
  }

  const total = yesValue + noValue;
  return total > 0 ? (yesValue / total) * 100 : 0;
});

const noSectionWidth = computed(() => {
  const yesValue = yesBidValue.value;
  const noValue = noBidValue.value;

  if (noValue === null || noValue === undefined || noValue <= 0) return 0;

  if (yesValue === null || yesValue === undefined || yesValue <= 0) {
    return noValue * 100;
  }

  const total = yesValue + noValue;
  return total > 0 ? (noValue / total) * 100 : 0;
});

const hasValidData = computed(() => {
  return (yesBidValue.value !== null && yesBidValue.value >= 0) || (noBidValue.value !== null && noBidValue.value >= 0);
});

const isDataLoading = computed(() => {
  return yesBidValue.value === null && noBidValue.value === null;
});

const { openOrderTicket } = useOrderTicket();
const actions = useContractActions();

const handleBidClick = (conid: number) => {
  if (appState.isScrolling) {
    return;
  }
  window.$apm?.logUserInteractionEvent('Card Click', 'contract-click', { origin: 'YesNoBidSingleStrike' }, false);
  if (props.productConid) {
    let exchange = props.routingExchange ?? props.exchange;
    let conidYes = props.yesConid;
    if (props.yesConidExPairs && props.yesConidExPairs.length > 0) {
      const [firstConid, firstExchange] = props.yesConidExPairs[0].split('@');
      conidYes = Number(firstConid);
      exchange = firstExchange;
    }
    actions.openQuoteDetails(props.productConid, exchange, conidYes);
    return;
  }
  const eventConidExs = props.multiExchange
    ? conid === props.yesConid
      ? props.yesConidExPairs
      : props.noConidExPairs
    : undefined;
  openOrderTicket(conid, undefined, props.multiExchange ? 'BEST' : undefined, eventConidExs);
};
</script>
<!-- last modified by AI tools on 2026-10-12 -->
